import styles from '../../opm-summary-waterfall-erf.module.scss';
import { SelectOption } from '@app/shared/models/option';
import { GridSelectField } from '@app/shared/components/grid-controls/grid-select-field/GridSelectField';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { FC } from 'react';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { positiveOrNullValidator } from '@app/core/validations/hook-forms/validators';
import { numericBaseFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { useLocale } from '@app/core/hooks/useLocale';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { OpmSimulatedValue } from '@app/shared/models/contracts/enums/shared-enums';
import SvgActionDelete from '@app/shared/icons/DeleteIcon';
import { cloneToMutableData, enumKeyByValue } from '@app/shared/helpers';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance } from '@app/shared/components/button/button-enums';
import { projectSlice } from '@app/core/store/project-slice';

interface SampleWaterfallInputsProps {
  yearOptions: SelectOption[];
  columnKeys: string[];
  shouldDisplayOutputs: boolean | undefined;
}

const SampleWaterfallInputs: FC<SampleWaterfallInputsProps> = ({
  yearOptions,
  columnKeys,
  shouldDisplayOutputs,
}): JSX.Element => {
  const { l } = useLocale();
  const dispatch = useAppDispatch();
  const project = useAppSelector((state) => state.project.projectDraft);
  const opmCalc = useAppSelector((state) => state.opmCalculation);

  const handleWaterfallDelete = (key: string) => {
    const updatedWaterfallInputs = cloneToMutableData(project.opmInput.exitScenarios ?? {});
    delete updatedWaterfallInputs[key];
    dispatch(
      projectSlice.actions.updateProjectDraftInternal({
        ...project,
        opmInput: {
          ...project.opmInput,
          exitScenarios: updatedWaterfallInputs,
        },
      })
    );
  };

  const exitValueType =
    opmCalc?.riskFreeValues?.inputs?.opmSimulatedValue ===
    enumKeyByValue(OpmSimulatedValue, OpmSimulatedValue.EnterpriseValue)
      ? l('_ExitEV')
      : l('_ExitEquity');

  const showDeleteButton = columnKeys.length > 1;

  return (
    <>
      <thead className="table-primary__sticky-section table-primary__sticky-section--table-single-row-header">
        <tr>
          <th className={styles['section-header']}>{l('_Inputs')}</th>
          {columnKeys.map((columnKey) => (
            <th key={columnKey} className={styles['section-header']}>
              {showDeleteButton && (
                <Button
                  startIcon={<SvgActionDelete />}
                  onClick={() => handleWaterfallDelete(columnKey)}
                  appearance={ButtonAppearance.CLEAN}
                />
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{l('_Year')}</td>
          {columnKeys.map((columnKey) => (
            <td key={columnKey} className="table-primary__cell--vertical-separator">
              <GridSelectField
                aria-label={l('_SelectorAriaLabel', { label: l('_Year') })}
                name={`exitScenarios.${columnKey}.exitDate`}
                options={yearOptions}
                data-testid={`year-select-${columnKey}`}
              />
            </td>
          ))}
        </tr>
        <tr>
          <td>{exitValueType}</td>
          {columnKeys.map((columnKey) => (
            <td key={columnKey} className="table-primary__cell--vertical-separator">
              <GridTextField
                name={`exitScenarios.${columnKey}.enterpriseValue`}
                data-testid={`enterprise-value-${columnKey}`}
                alignment={Alignment.Right}
                rules={{ ...positiveOrNullValidator(true) }}
                {...numericBaseFieldFormattingProps}
                placeholder="0"
              />
            </td>
          ))}
        </tr>
        {shouldDisplayOutputs && (
          <tr>
            <th colSpan={columnKeys.length + 1} className={styles['section-header']}>
              {l('_Calculations')}
            </th>
          </tr>
        )}
      </tbody>
    </>
  );
};

export default SampleWaterfallInputs;
